import {
  Course,
  CourseEntity,
  CourseQuery,
  CourseQueryVariables,
  FetchLatestCoursesQuery,
  FetchLatestCoursesQueryVariables,
  FetchCoursesQuery,
  LearnEntity,
  FetchCoursesPageableQuery,
  FetchCoursesPageableQueryVariables,
  Pagination,
  CoursePageQuery,
  CoursePage,
} from '@/graphql/generated/graphql';

import { initializeApollo } from '../lib/apollo-client';
import requestHandler from '../requestHandler';

import {
  QUERY_COURSE,
  FETCH_COURSES,
  QUERY_FETCH_LATEST_COURSES,
  QUERY_COURSE_LESSON,
  FETCH_COURSES_PAGEABLE,
  QUERY_COURSE_PAGE,
} from '@/graphql/queries/learn/course';

import { getAuthors } from '@/utils/learn';
import { RelatedContent } from '@/components/DefaultContentTemplate/RelatedContent';
import { joinWithCommaAnd } from '@/utils/array';
import { SearchTabFilters } from '../knowledge-hub/resource.service';

export const fetchCourseBySlug = async (slug: string) => {
  const apolloClient = initializeApollo();

  const { data, error } = await requestHandler(
    apolloClient.query<CourseQuery, CourseQueryVariables>({
      query: QUERY_COURSE,
      fetchPolicy: 'cache-first',
      variables: {
        slug: [slug],
      },
    }),
  );

  if (error) {
    console.error('Error fetching course', error);
    return null;
  }

  if (!data?.courses?.data.length) {
    return null;
  }

  return data?.courses?.data[0] as CourseEntity | null;
};

export const fetchCourseLessonBySlug = async (slug: string) => {
  const apolloClient = initializeApollo();

  const { data, error } = await requestHandler(
    apolloClient.query<CourseQuery, CourseQueryVariables>({
      query: QUERY_COURSE_LESSON,
      fetchPolicy: 'cache-first',
      variables: {
        slug: [slug],
      },
    }),
  );

  if (error) {
    console.error('Error fetching course', error);
    return null;
  }

  if (!data?.courses?.data.length) {
    return null;
  }

  return data?.courses?.data[0] as CourseEntity | null;
};

export const fetchRelatedContent = async (slug: string) => {
  const apolloClient = initializeApollo();

  const { data, error } = await requestHandler(
    apolloClient.query<FetchLatestCoursesQuery, FetchLatestCoursesQueryVariables>({
      query: QUERY_FETCH_LATEST_COURSES,
      fetchPolicy: 'cache-first',
      variables: {
        slug: [slug],
      },
    }),
  );

  if (error) {
    console.error('Error fetching course', error);
    return null;
  }

  if (data?.courses?.data?.length === 0) {
    return null;
  }

  const relatedContent = data?.courses?.data.map((course) => {
    return {
      title: course?.attributes?.title || '',
      description: course?.attributes?.description || '',
      author: joinWithCommaAnd(getAuthors(course?.attributes as Course)?.map((author) => author?.attributes?.name)),
      link: `/learn/course/${course?.attributes?.slug}`,
      type: course?.attributes?.type?.data?.attributes?.name || '',
      image: {
        url: course?.attributes?.thumbnail?.data?.attributes?.url || '',
        alternativeText: course?.attributes?.thumbnail?.data?.attributes?.alternativeText || '',
      },
    };
  });

  return relatedContent as Array<RelatedContent>;
};

export const fetchCourses = async () => {
  const apolloClient = initializeApollo();

  const { data, error } = await requestHandler(
    apolloClient.query<FetchCoursesQuery>({
      query: FETCH_COURSES,
      fetchPolicy: 'cache-first',
      variables: {
        limit: 10,
      },
    }),
  );

  if (error) {
    console.error('Error fetching courses', error);
    return null;
  }

  if (!data?.courses?.data.length) {
    return null;
  }

  return data?.courses?.data as Array<CourseEntity>;
};

export const fetchCoursesPageable = async (page: number, filters: SearchTabFilters) => {
  const apolloClient = initializeApollo();

  const { search, theme, topic } = filters;

  const { data, error } = await requestHandler(
    apolloClient.query<FetchCoursesPageableQuery, FetchCoursesPageableQueryVariables>({
      query: FETCH_COURSES_PAGEABLE,
      fetchPolicy: 'cache-first',
      variables: {
        page,
        search,
        theme: theme === 'null' ? undefined : theme,
        topic: topic === 'null' ? undefined : topic,
      },
    }),
  );

  if (error) {
    console.error('Error fetching courses', error);
    return { courses: [], pagination: {} as Pagination };
  }

  if (!data?.courses?.data.length) {
    return { courses: [], pagination: {} as Pagination };
  }

  return {
    courses: data?.courses?.data as CourseEntity[],
    pagination: data?.courses?.meta.pagination as Pagination,
  };
};

export const normalizeLearnItems = (learn: LearnEntity | null) => {
  if (!learn) {
    return null;
  }

  return learn?.attributes?.learn_items?.data?.map((learnItem) => learnItem?.attributes) || [];
};

export const fetchCourseHome = async () => {
  const apolloClient = initializeApollo();

  const { data, error } = await requestHandler(
    apolloClient.query<FetchCoursesQuery>({
      query: FETCH_COURSES,
      fetchPolicy: 'cache-first',
      variables: {
        limit: 1,
        publishedAt: 'publishedAt:desc',
      },
    }),
  );

  if (error) {
    console.error('Error fetching courses', error);
    return null;
  }

  if (!data?.courses?.data.length) {
    return null;
  }

  return data?.courses?.data[0].attributes as Course;
};

export const getPage = async () => {
  const apolloClient = initializeApollo();

  const { data, error } = await requestHandler(
    apolloClient.query<CoursePageQuery>({
      query: QUERY_COURSE_PAGE,
      fetchPolicy: 'cache-first',
    }),
  );

  return {
    page: data?.coursePage?.data?.attributes as CoursePage,
    error,
  };
};
